import React, { useEffect, useState } from "react";
import { Form, Field, FormElement } from "@progress/kendo-react-form";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { FormInput, FormTextArea, FormCheckbox } from "../FormComponents";
import { requiredValidator, urlValidator } from "../Validators";
import { Link } from "../../../data/linkData";

interface EditLinkFormProps {
  link: Link;
  onSubmit: (dataItem: any) => void;
  onDelete?: (linkId: number) => void;
  onCancel?: () => void;
  readOnly?: boolean;
}

const EditLinkForm = ({ link, onSubmit, onDelete, onCancel, readOnly = false }: EditLinkFormProps) => {
  const [visible, setVisible] = useState(false);

  const toggleDeleteDialog = () => {
    setVisible(!visible);
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete(link.linkId);
      setVisible(false);
    }
  };

  const handleSubmit = (dataItem: any) => {
    // Format data for API
    const linkData = {
      linkId: link.linkId,
      name: dataItem.name,
      description: dataItem.description || "",
      url: dataItem.url,
      enabled: dataItem.enabled !== undefined ? dataItem.enabled : true,
    };
    onSubmit(linkData);
  };

  return (
    <>
      <Form
        key={link.linkId}
        onSubmit={handleSubmit}
        initialValues={{
          name: link.name,
          description: link.description,
          url: link.url,
          enabled: link.enabled,
        }}
        render={(formRenderProps) => (
        <FormElement style={{ padding: "1rem", marginBottom: "5rem" }}>
          <fieldset className={"k-form-fieldset"} disabled={readOnly}>
            <div className="form__subtitle">
              {readOnly ? "Link details:" : "Edit link details:"}
            </div>
            {readOnly && <p className="k-form-hint">You have view-only access to links.</p>}

            <Field
              name={"name"}
              component={FormInput}
              disabled={readOnly}
              defaultValue={link.name}
              label={"Name: "}
              placeholder={"Enter link name"}
              validator={requiredValidator}
            />

            <Field
              name={"description"}
              component={FormTextArea}
              disabled={readOnly}
              label={"Description: "}
              placeholder={"Enter description"}
            />

            <Field
              name={"url"}
              component={FormInput}
              disabled={readOnly}
              label={"URL: "}
              defaultValue={link.url}
              placeholder={"https://example.com"}
              validator={urlValidator}
            />

            <Field
              name={"enabled"}
              component={FormCheckbox}
              disabled={readOnly}
              label={"Enabled"}
            />
          </fieldset>

          {!readOnly && (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              bottom: 0,
              left: 0,
              width: "100%",
              padding: "1rem 2rem 1rem 2rem",
              position: "absolute",
            }}
          >
            <button
              type={"submit"}
              disabled={!formRenderProps.allowSubmit}
              className="form__edit__button edit"
            >
              Save changes to Link
            </button>
            {onDelete && (
              <button
                type="button"
                onClick={toggleDeleteDialog}
                className="form__edit__button reject"
              >
                Mark Link as Deleted
              </button>
            )}
          </div>
          )}
        </FormElement>
      )}
      />
      {visible && (
        <Dialog title={"Please confirm"} onClose={toggleDeleteDialog}>
          <p
            style={{
              margin: "25px",
              textAlign: "center",
            }}
          >
            Are you sure you want to delete this link?
          </p>
          <DialogActionsBar>
            <button className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base" onClick={toggleDeleteDialog}>
              No
            </button>
            <button className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base" onClick={handleDelete}>
              Yes
            </button>
          </DialogActionsBar>
        </Dialog>
      )}
    </>
  );
};

export default EditLinkForm;
